import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useAuthProvider, useNotify } from 'react-admin';
import { Identity } from '../../../interfaces/auth';
import { BillingNoteResponse, RPMNoteContentAsHtml } from '../../../interfaces/billing.rpm';
import { BillingService } from '../../../services';
import { formatDateStringToMMDDYYYY } from '../../../util/dateFormat';
import { TextEditor, TextEditorRef } from '../../common/TextEditor';
import AlertDialog from '../common/AlertDialog';
import { SectionContent } from './SectionContent';
import './note-modal.css';

interface NotePreviewModalProps {
  open: boolean;
  onClose: () => void;
  claimId: number;
}

export const RPMNotePreviewModal: FC<NotePreviewModalProps> = ({ open, onClose, claimId }) => {
  const [note, setNote] = useState<BillingNoteResponse | null>(null);
  const [noteContentRawHtml, setNoteContentRawHtml] = useState<RPMNoteContentAsHtml>({
    header: '',
    body: '',
    footer: '',
  });
  const [editModeEnabled, setEditModeEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ongoingChangesStatus, setOngoingChangesStatus] = useState<
    'idle' | 'request-update' | 'updating' | 'request-reset' | 'resetting'
  >('idle');

  const [rteHeaderRef, rteBodyRef, rteFooterRef] = [
    useRef<TextEditorRef>(null),
    useRef<TextEditorRef>(null),
    useRef<TextEditorRef>(null),
  ];
  const authProvider = useAuthProvider();
  const notify = useNotify();

  const changeEditMode = () => {
    setEditModeEnabled((prevState) => !prevState);
  };

  const handleUpdateNoteContent = async () => {
    setOngoingChangesStatus('updating');
    const identity = (await authProvider.getIdentity!()) as Identity;
    if (!note || !identity?.fullName || !identity?.uuid) {
      notify(`Can't update note content`, 'error');
    } else {
      const billingServiceClient = new BillingService();
      try {
        const updatedNote = await billingServiceClient.updateNoteContent(
          note.id,
          {
            header: rteHeaderRef.current?.getHTML() ?? '',
            body: rteBodyRef.current?.getHTML() ?? '',
            footer: rteFooterRef.current?.getHTML() ?? '',
          },
          { fullName: identity.fullName, uuid: identity.uuid },
        );
        if (updatedNote.currentEditableContent) {
          setNoteContentRawHtml(updatedNote.currentEditableContent);
        }
        setEditModeEnabled(false);
      } catch (err) {
        notify('There was an error while updating the note content', 'error');
        console.error(err);
      }
    }
    setOngoingChangesStatus('idle');
  };

  const handleResetNoteContent = async () => {
    setOngoingChangesStatus('resetting');
    const identity = (await authProvider.getIdentity!()) as Identity;
    if (!note || !identity?.fullName || !identity?.uuid) {
      notify(`Can't reset note content`, 'error');
    } else {
      const billingServiceClient = new BillingService();
      try {
        const updatedNote = await billingServiceClient.resetNoteContent(note.id, {
          fullName: identity.fullName,
          uuid: identity.uuid,
        });
        if (updatedNote.currentEditableContent) {
          setNoteContentRawHtml(updatedNote.currentEditableContent);
        }
        setEditModeEnabled(false);
      } catch (err) {
        notify('There was an error while resetting the note content', 'error');
        console.error(err);
      }
    }
    setOngoingChangesStatus('idle');
  };

  useEffect(() => {
    if (!open) {
      setNote(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    const fetchNote = async () => {
      if (!claimId || !open) return;

      setIsLoading(true);
      setError(null);

      try {
        const billingService = new BillingService();
        const response = await billingService.getRpmNote(claimId);

        if (!response) {
          setError('Failed to load note data');
        } else {
          setNote(response);
          setNoteContentRawHtml({
            header: response.html.header,
            body: response.html.body,
            footer: response.html.footer,
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error fetching note');
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchNote();
    }
  }, [claimId, open]);

  const handleClose = () => {
    setNote(null);
    setError(null);
    onClose();
  };

  const renderAlertDialog = () => {
    if (ongoingChangesStatus === 'idle') {
      return null;
    }

    const requestUpdate = ongoingChangesStatus === 'request-update';

    return (
      <AlertDialog
        open={requestUpdate || ongoingChangesStatus === 'request-reset'}
        title={requestUpdate ? 'Save Changes' : 'Discard Changes'}
        content={
          requestUpdate
            ? 'Are you sure you want to save the changes made to this note?'
            : 'You are about to revert all your changes made to this note. Would you like to continue?'
        }
        rightAction={{
          buttonText: requestUpdate ? 'Save' : 'Discard',
          onClick: () => {
            if (requestUpdate) {
              handleUpdateNoteContent();
            } else {
              handleResetNoteContent();
            }
          },
        }}
        leftAction={{
          buttonText: 'Cancel',
          onClick: () => setOngoingChangesStatus('idle'),
        }}
      />
    );
  };

  if (!claimId) {
    return null;
  }

  return (
    <>
      {renderAlertDialog()}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        // Add cleanup on exit
        TransitionProps={{
          onExited: () => {
            setNote(null);
            setError(null);
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>Draft RPM Note</Typography>
            {!!note?.html && (
              <IconButton onClick={changeEditMode}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            {note && editModeEnabled && (
              <>
                <Button
                  color='success'
                  disabled={ongoingChangesStatus !== 'idle'}
                  onClick={() => setOngoingChangesStatus('request-update')}
                  variant='contained'>
                  Save
                </Button>
                <Button
                  color='error'
                  disabled={ongoingChangesStatus !== 'idle'}
                  onClick={() => setOngoingChangesStatus('request-reset')}
                  variant='contained'>
                  Reset
                </Button>
              </>
            )}
            <IconButton onClick={handleClose} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {note ? (
            <>
              {editModeEnabled && (
                <Box display='flex' flexDirection='column' gap={4}>
                  <TextEditor
                    content={noteContentRawHtml.header}
                    ref={rteHeaderRef}
                    title='Header'
                  />
                  <TextEditor content={noteContentRawHtml.body} ref={rteBodyRef} title='Body' />
                  <TextEditor
                    content={noteContentRawHtml.footer}
                    ref={rteFooterRef}
                    title='Footer'
                  />
                </Box>
              )}
              {!editModeEnabled && !note.html && (
                <Box>
                  <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography>
                          <strong>Patient:</strong> {note.note.header.patient}
                        </Typography>
                        <Typography>
                          <strong>DOB:</strong>{' '}
                          {formatDateStringToMMDDYYYY(note.note.header.dateOfBirth)}
                        </Typography>
                        <Typography>
                          <strong>Sex:</strong> {note.note.header.sex}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography>
                          <strong>Chart #:</strong> {note.note.header.chartNumber}
                        </Typography>
                        <Typography>
                          <strong>Provider:</strong> {note.note.header.provider}
                        </Typography>
                        <Typography>
                          <strong>Visit Date:</strong>{' '}
                          {formatDateStringToMMDDYYYY(note.note.header.visitDate)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {note.note.sections
                    .sort((a, b) => a.order - b.order)
                    .map((section) => (
                      <Box key={section.key} sx={{ mb: 3 }}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
                          {section.title}
                        </Typography>
                        <SectionContent section={section} />
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}

                  <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography>{note.note.footer.centerText}</Typography>
                    <Typography variant='caption'>
                      {note.note.footer.datetime} - {note.note.footer.providerFullName}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      {note.note.footer.poweredBy}
                    </Typography>
                  </Box>
                </Box>
              )}
              {!editModeEnabled && !!note.html && (
                <Box>
                  <div
                    dangerouslySetInnerHTML={{ __html: noteContentRawHtml.header }}
                    className='editable-header'
                  />
                  <hr />
                  <div
                    dangerouslySetInnerHTML={{ __html: noteContentRawHtml.body }}
                    className='editable-body'
                  />
                  <hr />
                  <div
                    dangerouslySetInnerHTML={{ __html: noteContentRawHtml.footer }}
                    className='editable-footer'
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              {isLoading && (
                <Box display='flex' justifyContent='center' alignItems='center' height={200}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Typography color='error' align='center'>
                  {error}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
